import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tablesAPI, waitlistAPI, staffAPI, shiftsAPI, superAdminAPI, clubsAPI } from '../lib/api';
import { sanitizeTransactionNotesForDisplay } from '../lib/transactionNotes';
import { getCachedClubLogo, setCachedClubLogo } from '../lib/clubLogoCache';
import { useRummyTableBuyInOutPending } from '../hooks/useTableBuyInOutPending';
import toast from 'react-hot-toast';
import TableBuyOutManagement from './TableBuyOutManagement';
import RakeCollection from './RakeCollection';
import { getWebSocketBaseUrl } from '../lib/websocketUrl';

function isStaffSessionPausedNotes(notes) {
  if (!notes || typeof notes !== 'string') return false;
  return /Paused Elapsed:\s*\d+/i.test(notes) && !/Session Started:/i.test(notes);
}

function hasStaffTableSessionStarted(notes) {
  if (!notes || typeof notes !== 'string') return false;
  return (
    /Session Started:/i.test(notes) ||
    /Paused At:/i.test(notes) ||
    /Paused Elapsed:\s*\d+/i.test(notes)
  );
}

function formatTableSessionHms(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function getTableSessionElapsedSecondsFromNotes(notes, now = new Date()) {
  if (!notes || typeof notes !== 'string') return 0;
  if (isStaffSessionPausedNotes(notes)) {
    const m = notes.match(/Paused Elapsed:\s*(\d+)/i);
    const v = m ? parseInt(m[1], 10) : 0;
    return Number.isFinite(v) && v >= 0 ? v : 0;
  }
  const startM = notes.match(/Session Started:\s*([^|]+)/i);
  if (startM?.[1]) {
    const t = new Date(String(startM[1]).trim());
    if (Number.isNaN(t.getTime())) return 0;
    const carryM = notes.match(/Paused Elapsed:\s*(\d+)/i);
    const carry = carryM ? parseInt(carryM[1], 10) : 0;
    const c = Number.isFinite(carry) && carry > 0 ? carry : 0;
    return Math.max(0, Math.floor((now.getTime() - t.getTime()) / 1000) + c);
  }
  return 0;
}

/**
 * Comprehensive Rummy Table Management Component
 * Features:
 * 1. Live Tables - Hologram view with seat status
 * 2. Table Management - CRUD operations (Rummy-specific)
 * 3. Table Buy-In - Waitlist, seat allocation, reorder
 * 4. Table Buy-Out - Player requests, admin approval
 */
export default function RummyTableManagement({ selectedClubId, permissions = {} }) {
  const [activeTab, setActiveTab] = useState("live-tables");
  const queryClient = useQueryClient();
  const canManageTables = permissions.canManageTables !== false;
  const canAssignSeat = permissions.canAssignSeat !== false;

  const {
    pendingRummyBuyOutCount,
    pendingRummyBuyOutTotal,
    pendingRummyBuyInRequestCount,
    pendingRummyBuyInTabBadgeCount,
  } = useRummyTableBuyInOutPending(selectedClubId, { enableAlertSound: true, enabled: !!selectedClubId });

  // Fetch tables (filter for rummy tables)
  const { data: tablesData, isLoading: tablesLoading } = useQuery({
    queryKey: ['rummy-tables', selectedClubId],
    queryFn: () => tablesAPI.getTables(selectedClubId),
    enabled: !!selectedClubId,
    select: (data) => {
      // Filter for rummy tables
      return Array.isArray(data) ? data.filter(t => t.tableType === 'RUMMY') : [];
    }
  });

  // Fetch waitlist
  const { data: waitlistData, isLoading: waitlistLoading } = useQuery({
    queryKey: ['waitlist', selectedClubId],
    queryFn: () => waitlistAPI.getWaitlist(selectedClubId),
    enabled: !!selectedClubId,
  });

  const tables = tablesData || [];
  const waitlist = waitlistData || [];

  const allTabs = [
    { id: "live-tables", label: "Live Rummy Tables" },
    { id: "table-management", label: "Table Management" },
    { id: "table-buy-in", label: "Table Buy-In" },
    { id: "table-buy-out", label: "Table Buy-Out" },
    { id: "rake-collection", label: "Rake Collection" },
    { id: "history", label: "History" },
  ];
  const requestedVisibleTabs = Array.isArray(permissions.visibleTabs) ? permissions.visibleTabs : null;
  const tabs = requestedVisibleTabs
    ? allTabs.filter((tab) => requestedVisibleTabs.includes(tab.id))
    : allTabs;

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id || "live-tables");
    }
  }, [tabs, activeTab]);

  return (
    <div className="text-white space-y-6">
      <h1 className="text-3xl font-bold">Rummy Tables & Waitlist</h1>

      {!selectedClubId && (
        <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4 text-yellow-100 flex items-center gap-3">
          <svg className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.3 2.647-1.3 3.412 0l7 12a1.5 1.5 0 01-1.302 2.25H2.847a1.5 1.5 0 01-1.302-2.25l7-12zM9 13a1 1 0 112 0v2a1 1 0 11-2 0v-2zm0-6a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
          <p className="font-medium">Please select a club from the sidebar dropdown to manage rummy tables.</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 flex-wrap">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          let title;
          let content = tab.label;

          if (selectedClubId && tab.id === 'table-buy-in') {
            title =
              pendingRummyBuyInTabBadgeCount > 0
                ? `${pendingRummyBuyInTabBadgeCount} on waitlist${
                    pendingRummyBuyInRequestCount > 0
                      ? ` · ${pendingRummyBuyInRequestCount} seated table chip request(s)`
                      : ''
                  }`
                : pendingRummyBuyInRequestCount > 0
                  ? `${pendingRummyBuyInRequestCount} seated table chip buy-in request(s) for rummy tables`
                  : undefined;
            content =
              pendingRummyBuyInTabBadgeCount > 0 ? (
                <span className="inline-flex items-center gap-2">
                  <span>Table Buy-In</span>
                  <span
                    className={`text-xs font-bold rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center flex-shrink-0 text-white shadow-sm ${
                      isActive ? 'bg-red-500 ring-2 ring-red-300/80' : 'bg-red-600'
                    }`}
                  >
                    {pendingRummyBuyInTabBadgeCount > 99 ? '99+' : pendingRummyBuyInTabBadgeCount}
                  </span>
                </span>
              ) : (
                tab.label
              );
          } else if (selectedClubId && tab.id === 'table-buy-out') {
            title =
              pendingRummyBuyOutCount > 0
                ? `${pendingRummyBuyOutCount} pending · ₹${pendingRummyBuyOutTotal.toLocaleString('en-IN')} est. on table`
                : undefined;
            content =
              pendingRummyBuyOutCount > 0 ? (
                <span className="inline-flex items-center gap-2">
                  <span>Table Buy-Out</span>
                  <span
                    className={`text-xs font-bold rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center flex-shrink-0 text-white shadow-sm ${
                      isActive ? 'bg-red-500 ring-2 ring-red-300/80' : 'bg-red-600'
                    }`}
                  >
                    {pendingRummyBuyOutCount > 99 ? '99+' : pendingRummyBuyOutCount}
                  </span>
                </span>
              ) : (
                tab.label
              );
          }

          return (
            <button
              key={tab.id}
              type="button"
              title={title}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-semibold transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-700 text-white border-b-2 border-emerald-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {content}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Tab 1: Live Tables */}
        {activeTab === "live-tables" && (
          <LiveTablesView 
            selectedClubId={selectedClubId}
            tables={tables}
            tablesLoading={tablesLoading}
            canManageTables={canManageTables}
          />
        )}

        {/* Tab 2: Table Management */}
        {activeTab === "table-management" && (
          <RummyTableManagementView
            selectedClubId={selectedClubId}
            tables={tables}
            tablesLoading={tablesLoading}
            canManageTables={canManageTables}
          />
        )}

        {/* Tab 3: Table Buy-In */}
        {activeTab === "table-buy-in" && (
          <TableBuyInView
            selectedClubId={selectedClubId}
            tables={tables}
            waitlist={waitlist}
            waitlistLoading={waitlistLoading}
            canAssignSeat={canAssignSeat}
          />
        )}

        {/* Tab 4: Table Buy-Out */}
        {activeTab === "table-buy-out" && (
          <TableBuyOutView
            selectedClubId={selectedClubId}
            tables={tables}
          />
        )}

        {/* Tab 5: Rake Collection */}
        {activeTab === "rake-collection" && (
          <RakeCollection clubId={selectedClubId} gameType="rummy" />
        )}

        {/* Tab 6: History */}
        {activeTab === "history" && (
          <RummyHistoryView
            selectedClubId={selectedClubId}
            tables={tables}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Live Tables View Component
// ============================================================================
function LiveTablesView({ selectedClubId, tables, tablesLoading, canManageTables = true }) {
  const [selectedTable, setSelectedTable] = useState(null);
  const [showSessionControl, setShowSessionControl] = useState(false);
  const [selectedTableForControl, setSelectedTableForControl] = useState(null);
  const [sessionParams, setSessionParams] = useState({
    minPlayTime: 30,
    callTime: 5,
    cashOutWindow: 10,
    sessionTimeout: 120,
  });
  const [sessionStatus, setSessionStatus] = useState('Active');

  const activeTables = tables.filter(t => t.status === 'AVAILABLE' || t.status === 'OCCUPIED');
  const startedSessionTables = activeTables.filter((t) => hasStaffTableSessionStarted(t.notes));
  const sessionControlTable =
    showSessionControl && selectedTableForControl && Array.isArray(tables)
      ? tables.find((t) => t.id === selectedTableForControl.id) || selectedTableForControl
      : selectedTableForControl;

  // Helper function to extract dealer name from notes
  const getDealerName = (table) => {
    if (!table.notes) return null;
    const dealerMatch = table.notes.match(/Dealer:.*?\(([^)]+)\)/i);
    return dealerMatch ? dealerMatch[1] : null;
  };

  const handleEditSession = (table) => {
    setSelectedTableForControl(table);
    setShowSessionControl(true);
    
    // Parse session parameters from notes if available
    const noteParts = table.notes ? table.notes.split('|').map(p => p.trim()) : [];
    const minPlayTime = noteParts[3]?.match(/\d+/)?.[0] || '30';
    const callTime = noteParts[4]?.match(/\d+/)?.[0] || '5';
    const cashOutWindow = noteParts[5]?.match(/\d+/)?.[0] || '10';
    const sessionTimeout = noteParts[6]?.match(/\d+/)?.[0] || '120';
    
    setSessionParams({
      minPlayTime: parseInt(minPlayTime),
      callTime: parseInt(callTime),
      cashOutWindow: parseInt(cashOutWindow),
      sessionTimeout: parseInt(sessionTimeout),
    });
    
    setSessionStatus(isStaffSessionPausedNotes(table.notes) ? 'Paused' : 'Active');
  };

  return (
    <div className="space-y-6">
      {/* Table Session Control Panel */}
      {showSessionControl && selectedTableForControl && (
        <TableSessionControl
          table={sessionControlTable}
          tables={startedSessionTables}
          sessionParams={sessionParams}
          setSessionParams={setSessionParams}
          sessionStatus={sessionStatus}
          setSessionStatus={setSessionStatus}
          clubId={selectedClubId}
          onClose={() => {
            setShowSessionControl(false);
            setSelectedTableForControl(null);
          }}
          onSelectTable={(table) => {
            setSelectedTableForControl(table);
            handleEditSession(table);
          }}
        />
      )}

      {!showSessionControl && (
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Live Rummy Tables - Hologram View</h2>
            <p className="text-gray-400 text-sm mt-1">Manage live rummy tables, seat players, and handle buy-ins using table hologram.</p>
          </div>
          <div className="bg-slate-700 px-4 py-2 rounded-lg">
            <div className="text-sm text-gray-300">Live sessions: {startedSessionTables.length}</div>
            <div className="text-sm text-gray-300">Total Players: {startedSessionTables.reduce((sum, t) => sum + (t.currentSeats || 0), 0)}</div>
          </div>
        </div>

        {tablesLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
            <p>Loading rummy tables...</p>
          </div>
        ) : startedSessionTables.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🃏</div>
            <p className="text-xl text-gray-300">No live rummy sessions</p>
            <p className="text-gray-400 text-sm mt-2">Start a session from Table Management — only tables with a running or paused session appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {startedSessionTables.map((table) => (
              <div
                key={table.id}
                className="bg-slate-700 rounded-xl p-5 border border-slate-600 hover:border-emerald-500 transition-all cursor-pointer"
                onClick={() => setSelectedTable(table)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-white">Table {table.tableNumber}</h3>
                    <p className="text-sm text-gray-400">{table.rummyVariant || 'RUMMY'}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    table.status === 'OCCUPIED' ? 'bg-green-600/20 text-green-400' : 'bg-emerald-600/20 text-emerald-400'
                  }`}>
                    {table.status}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Stakes:</span>
                    <span className="text-white font-medium">₹{table.minBuyIn || 0}/₹{table.maxBuyIn || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Occupied Seats:</span>
                    <span className="text-white font-medium">{table.currentSeats || 0} / {table.maxSeats}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Available Seats:</span>
                    <span className="text-emerald-400 font-medium">{table.maxSeats - (table.currentSeats || 0)}</span>
                  </div>
                  {getDealerName(table) && (
                    <div className="flex justify-between text-sm pt-1 border-t border-slate-600">
                      <span className="text-gray-400">👤 Dealer:</span>
                      <span className="text-purple-400 font-semibold">{getDealerName(table)}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTable(table);
                  }}
                  className="w-full mt-4 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <span>🎯</span>
                  <span>View Table Hologram</span>
                </button>
                
                {canManageTables && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditSession(table);
                    }}
                    className="w-full mt-2 bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-500 hover:to-pink-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    <span>⚙️</span>
                    <span>Edit Session Control</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Table Hologram Modal */}
      {selectedTable && (
        <TableHologramModal
          table={selectedTable}
          onClose={() => setSelectedTable(null)}
          clubId={selectedClubId}
        />
      )}
    </div>
  );
}

// ============================================================================
// Table Session Control Component  
// ============================================================================
function TableSessionControl({ 
  table, 
  tables, 
  sessionParams, 
  setSessionParams, 
  sessionStatus, 
  setSessionStatus,
  clubId,
  onClose,
  onSelectTable 
}) {
  const queryClient = useQueryClient();
  const [selectedClubId, setSelectedClubId] = useState(clubId);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [seatedPlayers, setSeatedPlayers] = useState([]);
  const [settlements, setSettlements] = useState({});
  const [rakeAmount, setRakeAmount] = useState('');
  const [tableSessionHms, setTableSessionHms] = useState('00:00:00');

  // Update selectedClubId when clubId prop changes
  useEffect(() => {
    if (clubId) {
      setSelectedClubId(clubId);
    }
  }, [clubId]);

  useEffect(() => {
    if (!table?.notes) {
      setSessionStatus('Active');
      return;
    }
    setSessionStatus(isStaffSessionPausedNotes(table.notes) ? 'Paused' : 'Active');
  }, [table?.id, table?.notes, setSessionStatus]);

  useEffect(() => {
    if (!table) return;
    const tick = () =>
      setTableSessionHms(formatTableSessionHms(getTableSessionElapsedSecondsFromNotes(table.notes)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [table?.id, table?.notes]);

  // Pause session mutation
  const pauseSessionMutation = useMutation({
    mutationFn: async () => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.pauseSession(selectedClubId, table.id);
    },
    onSuccess: () => {
      setSessionStatus('Paused');
      toast.success('Session paused successfully');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to pause session');
    },
  });

  // Resume session mutation
  const resumeSessionMutation = useMutation({
    mutationFn: async () => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.resumeSession(selectedClubId, table.id);
    },
    onSuccess: () => {
      setSessionStatus('Active');
      toast.success('Session resumed successfully');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to resume session');
    },
  });

  // Settle and end session mutation (same as Poker)
  const settleAndEndMutation = useMutation({
    mutationFn: async (settlementsData) => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.settleAndEndSession(selectedClubId, table.id, settlementsData);
    },
    onSuccess: () => {
      toast.success('All players settled and session ended successfully!');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
      setShowSettlementModal(false);
      onClose();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to settle players');
    },
  });

  // End session mutation (only used when no players are seated)
  const endSessionMutation = useMutation({
    mutationFn: async () => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.endSession(selectedClubId, table.id);
    },
    onSuccess: () => {
      toast.success('Session ended successfully');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
      onClose();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to end session');
    },
  });

  // Update session params mutation
  const updateParamsMutation = useMutation({
    mutationFn: async (params) => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.updateSessionParams(selectedClubId, table.id, params);
    },
    onSuccess: (data) => {
      toast.success('Session parameters updated successfully');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
      if (data.sessionParams) {
        setSessionParams(data.sessionParams);
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update session parameters');
    },
  });

  const handlePauseSession = () => {
    const started = hasStaffTableSessionStarted(table.notes);
    if (!started) {
      resumeSessionMutation.mutate();
      return;
    }
    if (sessionStatus === 'Active') {
      pauseSessionMutation.mutate();
    } else {
      resumeSessionMutation.mutate();
    }
  };

  const handleEndSession = async () => {
    if (!selectedClubId) {
      toast.error('Club ID not found');
      return;
    }

    try {
      const response = await tablesAPI.getSeatedPlayersForTable(selectedClubId, table.id);
      
      if (response.seatedPlayers && response.seatedPlayers.length > 0) {
        setSeatedPlayers(response.seatedPlayers);
        
        const initialSettlements = {};
        response.seatedPlayers.forEach(player => {
          initialSettlements[player.playerId] = 0;
        });
        setSettlements(initialSettlements);
        setRakeAmount('');
        setShowSettlementModal(true);
      } else {
        if (window.confirm('No players are seated. End this session?')) {
          endSessionMutation.mutate();
        }
      }
    } catch (error) {
      console.error('Error fetching seated players:', error);
      toast.error('Failed to fetch seated players');
    }
  };

  const handleSettlementChange = (playerId, amount) => {
    setSettlements(prev => ({
      ...prev,
      [playerId]: parseFloat(amount) || 0
    }));
  };

  const handleConfirmSettlement = () => {
    const settlementsArray = Object.entries(settlements).map(([playerId, amount]) => ({
      playerId,
      amount: parseFloat(amount) || 0
    }));

    const rakeNum = parseFloat(rakeAmount) || 0;

    const totalAmount = settlementsArray.reduce((sum, s) => sum + s.amount, 0);
    const confirmMsg = rakeNum > 0
      ? `Settle ${settlementsArray.length} players for a total of \u20B9${totalAmount} with \u20B9${rakeNum} rake? This will end the session.`
      : `Settle ${settlementsArray.length} players for a total of \u20B9${totalAmount}? This will end the session.`;

    if (window.confirm(confirmMsg)) {
      settleAndEndMutation.mutate({ settlements: settlementsArray, rakeAmount: rakeNum || undefined });
    }
  };

  const handleSetParameter = (param, value) => {
    const params = { [param]: parseInt(value) };
    updateParamsMutation.mutate(params);
  };

  const getTableGameType = (t) => {
    return t.rummyVariant || t.notes?.split('|')[1]?.trim() || 'RUMMY';
  };

  const getTableStakes = (t) => {
    const noteParts = t.notes ? t.notes.split('|').map(p => p.trim()) : [];
    return noteParts[2]?.replace('Stakes:', '').trim() || `₹${t.minBuyIn || 0}/₹${t.maxBuyIn || 0}`;
  };

  return (
    <div className="bg-gradient-to-br from-emerald-900/50 via-teal-900/50 to-green-900/50 rounded-2xl p-8 border-2 border-emerald-500/30 shadow-2xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-white">Rummy Table Session Control</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Table Sessions */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-white mb-4">Table Sessions</h3>
          <div className="space-y-3">
            {tables.length === 0 ? (
              <p className="text-sm text-gray-400">
                No tables with a started staff session. Start a session with Resume on a table first.
              </p>
            ) : (
              tables.map((t) => (
              <div
                key={t.id}
                onClick={() => onSelectTable(t)}
                className={`p-4 rounded-lg cursor-pointer transition-all ${
                  t.id === table.id
                    ? 'bg-emerald-600/30 border-2 border-emerald-500'
                    : 'bg-slate-700/50 border border-slate-600 hover:bg-slate-700'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-white">Table {t.tableNumber} - {getTableGameType(t)}</div>
                    <div className="text-sm text-gray-400">{t.rummyVariant || 'Rummy'}</div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    isStaffSessionPausedNotes(t.notes)
                      ? 'bg-yellow-600/20 text-yellow-400'
                      : 'bg-green-600/20 text-green-400'
                  }`}>
                    {isStaffSessionPausedNotes(t.notes) ? 'Paused' : 'Running'}
                  </span>
                </div>
              </div>
            ))
            )}
          </div>

          {/* Selected Table Details */}
          <div className="mt-6 p-4 bg-emerald-600/20 border border-emerald-500/30 rounded-lg">
            <div className="text-sm text-emerald-300 mb-2">Selected: Table {table.tableNumber} - {getTableGameType(table)}</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-400">Variant:</span>
                <span className="text-white ml-2">{table.rummyVariant || 'Rummy'}</span>
              </div>
              <div>
                <span className="text-gray-400">Stakes:</span>
                <span className="text-white ml-2">{getTableStakes(table)}</span>
              </div>
            </div>
            <div className="text-sm mt-2">
              <span className="text-gray-400">Players:</span>
              <span className="text-white ml-2">{table.currentSeats || 0}/{table.maxSeats}</span>
            </div>
          </div>
        </div>

        {/* Center: Session Actions */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-white mb-4">Session Actions</h3>
          
          <div className="mb-6">
            <div className="text-gray-400 text-sm mb-2">Current Status</div>
            <div className={`text-4xl font-bold ${sessionStatus === 'Active' ? 'text-green-400' : 'text-yellow-400'}`}>
              {sessionStatus}
            </div>
            <div className="mt-5 rounded-xl bg-slate-900/60 border border-slate-600/80 px-4 py-3 text-center">
              <div className="text-gray-500 text-xs uppercase tracking-wide">Table session time</div>
              <div className="text-2xl font-mono font-bold text-white mt-1">{tableSessionHms}</div>
              <div className="text-xs text-gray-500 mt-1">Same clock as hologram · freezes when paused</div>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={handlePauseSession}
              className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2"
            >
              <span>
                {!hasStaffTableSessionStarted(table.notes)
                  ? '▶'
                  : sessionStatus === 'Active'
                    ? '⏸'
                    : '▶'}
              </span>
              <span>
                {!hasStaffTableSessionStarted(table.notes)
                  ? 'Start Table Session'
                  : sessionStatus === 'Active'
                    ? 'Pause Session'
                    : 'Resume Session'}
              </span>
            </button>

            <button
              onClick={handleEndSession}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2"
            >
              <span>⬛</span>
              <span>End Session</span>
            </button>
          </div>

          <div className="mt-6 p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-gray-400 text-center">
              Rummy tables have no minimum play time or call time — players can exit anytime.
            </p>
          </div>
        </div>

        {/* Right: Timing & Parameters */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-white mb-4">Session Settings</h3>
          
          <div className="space-y-6">
            {/* Rummy info banner */}
            <div className="p-3 bg-emerald-900/30 border border-emerald-500/30 rounded-lg">
              <div className="text-sm font-medium text-emerald-300 mb-1">Rummy Rules</div>
              <ul className="text-xs text-emerald-400 space-y-1">
                <li>• No minimum play time — players can exit anytime</li>
                <li>• No call time required</li>
                <li>• Rake can be collected multiple times per session</li>
              </ul>
            </div>

            {/* Session Timeout */}
            <div>
              <div className="text-gray-400 text-sm font-medium mb-2">SESSION TIMEOUT</div>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={sessionParams.sessionTimeout}
                  onChange={(e) => setSessionParams({ ...sessionParams, sessionTimeout: e.target.value })}
                  className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-semibold"
                  min="30"
                />
                <button
                  onClick={() => handleSetParameter('sessionTimeout', sessionParams.sessionTimeout)}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white font-semibold transition-colors"
                >
                  Set
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">Current: {sessionParams.sessionTimeout} mins</div>
            </div>
          </div>
        </div>
      </div>

      {/* Settlement Modal - same as Poker */}
      {showSettlementModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-emerald-500/50 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">🃏 Settle All Rummy Players</h3>
              <button
                onClick={() => setShowSettlementModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6 p-4 bg-red-600/20 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300">
                Enter the cash-out amount for each seated player. This will create transactions,
                update balances, unseat all players, and end the session.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              {seatedPlayers.map(player => (
                <div key={player.playerId} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-semibold text-white">{player.playerName}</div>
                      <div className="text-sm text-gray-400">Seat #{player.seatNumber}</div>
                    </div>
                    <div className="text-sm text-gray-400">
                      Seated {new Date(player.seatedAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
                    </div>
                  </div>
                  {/* Player Balance Info */}
                  <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
                    <div className="bg-slate-800/60 rounded px-3 py-2 text-center">
                      <div className="text-gray-400 text-xs">Buy-In</div>
                      <div className="text-cyan-400 font-semibold">₹{(player.buyInAmount || 0).toLocaleString()}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded px-3 py-2 text-center">
                      <div className="text-gray-400 text-xs">Wallet Balance</div>
                      <div className={`font-semibold ${(player.walletBalance || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ₹{(player.walletBalance || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-slate-800/60 rounded px-3 py-2 text-center">
                      <div className="text-gray-400 text-xs">Credits</div>
                      <div className="text-yellow-400 font-semibold">
                        {(player.totalCredits || 0) > 0 ? `₹${(player.totalCredits || 0).toLocaleString()}` : '-'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">Cash-out Amount:</span>
                    <input
                      type="number"
                      value={settlements[player.playerId] || 0}
                      onChange={(e) => handleSettlementChange(player.playerId, e.target.value)}
                      className="flex-1 px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-lg font-semibold"
                      placeholder="Enter amount"
                      min="0"
                      step="100"
                    />
                    <span className="text-white font-semibold">₹</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between p-4 bg-emerald-600/20 border border-emerald-500/30 rounded-lg mb-4">
              <span className="text-emerald-300 font-semibold">Total Settlement:</span>
              <span className="text-white text-2xl font-bold">
                ₹{Object.values(settlements).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0).toLocaleString()}
              </span>
            </div>

            {/* Rake Collection Input */}
            <div className="p-4 bg-amber-600/20 border border-amber-500/30 rounded-lg mb-6">
              <div className="flex items-center gap-3">
                <span className="text-amber-300 font-semibold whitespace-nowrap">🎰 Session Rake:</span>
                <input
                  type="number"
                  value={rakeAmount}
                  onChange={(e) => setRakeAmount(e.target.value)}
                  className="flex-1 px-4 py-2 bg-slate-600 border border-amber-500/50 rounded-lg text-white text-lg font-semibold"
                  placeholder="Enter rake amount (optional)"
                  min="0"
                  step="100"
                />
                <span className="text-white font-semibold">₹</span>
              </div>
              <p className="text-amber-300/70 text-xs mt-2">Rake will be recorded for this rummy session automatically</p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setShowSettlementModal(false)}
                className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSettlement}
                disabled={settleAndEndMutation.isLoading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
              >
                {settleAndEndMutation.isLoading ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>✅</span>
                    <span>Settle & End Session</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Table Hologram Modal Component
// ============================================================================
function TableHologramModal({ table: initialTable, onClose, clubId }) {
  const queryClient = useQueryClient();
  // Seed from localStorage cache so the logo paints on first render — no spade flicker.
  const [clubData, setClubData] = useState(() => {
    const cached = getCachedClubLogo(clubId);
    return cached ? { logoUrl: cached } : null;
  });
  const [clubFetched, setClubFetched] = useState(false);
  const [sessionTime, setSessionTime] = useState('00:00:00');
  const [seatedPlayersData, setSeatedPlayersData] = useState([]);
  const [activeTab, setActiveTab] = useState('view');
  const [tableHistory, setTableHistory] = useState([]);
  const [table, setTable] = useState(initialTable);
  useEffect(() => {
    setTable(initialTable);
  }, [initialTable]);
  const [showRakeForm, setShowRakeForm] = useState(false);
  const [rakeAmount, setRakeAmount] = useState('');
  const [rakeNotes, setRakeNotes] = useState('');

  const rakeCollectionMutation = useMutation({
    mutationFn: (data) => clubsAPI.createRakeCollection(clubId, data),
    onSuccess: () => {
      toast.success('Rake collected for this table. You can collect again during the session.');
      setRakeAmount('');
      setRakeNotes('');
      setShowRakeForm(false);
      queryClient.invalidateQueries(['rakeCollections', clubId]);
    },
    onError: (err) => toast.error(err.message || 'Failed to save rake collection'),
  });

  // Fetch table history (buy-ins and buy-outs for this table)
  useEffect(() => {
    const fetchTableHistory = async () => {
      if (activeTab !== 'history') return;
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        const tenantId = localStorage.getItem('tenantId');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3333/api'}/clubs/${clubId}/transactions?tableNumber=${table.tableNumber}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-id': userId || '',
            'x-tenant-id': tenantId || '',
            'x-club-id': clubId,
          },
        });
        if (response.ok) {
          const data = await response.json();
          const tableLabel = `Table ${table.tableNumber}`;
          const filtered = (Array.isArray(data) ? data : []).filter(t => {
            const isTableTxn = t.type === 'Table Buy In' || t.type === 'Table Buy Out' || t.type === 'Buy In' || (t.type === 'Deposit' && t.notes?.includes(tableLabel));
            const mentionsTable = (t.notes || '').includes(tableLabel) || (t.description || '').includes(tableLabel);
            return isTableTxn && mentionsTable;
          }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setTableHistory(filtered);
        }
      } catch (error) {
        console.error('Error fetching table history:', error);
      }
    };
    if (clubId && table?.tableNumber && activeTab === 'history') {
      fetchTableHistory();
    }
  }, [clubId, table?.tableNumber, activeTab]);

  // Fetch seated players for this table (same as poker)
  useEffect(() => {
    const fetchSeatedPlayers = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        const tenantId = localStorage.getItem('tenantId');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3333/api'}/clubs/${clubId}/tables/${table.id}/seated-players`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-id': userId || '',
            'x-tenant-id': tenantId || '',
            'x-club-id': clubId,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setSeatedPlayersData(data.seatedPlayers || []);
        }
      } catch (error) {
        console.error('Error fetching seated players:', error);
      }
    };
    
    if (clubId && table?.id) {
      fetchSeatedPlayers();

      // Drive updates via WebSocket — no polling.
      const wsBase = getWebSocketBaseUrl();
      if (!wsBase) return;
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || localStorage.getItem('token') || sessionStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const { io: socketIO } = require('socket.io-client');
      const socket = socketIO(`${wsBase}/realtime`, {
        auth: { clubId, userId, token },
        transports: ['websocket', 'polling'],
        reconnection: true,
      });
      socket.on('connect', () => socket.emit('subscribe:club', { clubId, userId }));
      socket.on('table:status-changed', (payload) => {
        if (payload?.table?.id === initialTable.id) {
          setTable((prev) => ({
            ...prev,
            ...payload.table,
            notes: payload.table?.notes != null ? payload.table.notes : prev.notes,
          }));
        }
        fetchSeatedPlayers();
      });
      socket.on('tables:updated', () => {
        queryClient.invalidateQueries(['rummy-tables', clubId]);
        fetchSeatedPlayers();
      });
      socket.on('waitlist:status-changed', fetchSeatedPlayers);
      socket.on('waitlist:position-updated', fetchSeatedPlayers);
      socket.on('transaction:new', fetchSeatedPlayers);
      socket.on('balance:updated', fetchSeatedPlayers);
      socket.on('buyin:updated', fetchSeatedPlayers);
      return () => { socket.disconnect(); };
    }
  }, [clubId, table?.id, initialTable?.id, queryClient]);

  // Extract dealer info from table notes
  const getDealerInfo = () => {
    if (!table.notes) return null;
    const dealerMatch = table.notes.match(/Dealer:\s*([a-f0-9-]{36})\s*\(([^)]+)\)/i);
    if (dealerMatch) {
      return { id: dealerMatch[1], name: dealerMatch[2] };
    }
    return null;
  };

  const dealerInfo = getDealerInfo();

  // Unassign dealer mutation
  const unassignDealerMutation = useMutation({
    mutationFn: async () => {
      if (!table || !dealerInfo) return;
      
      // Remove dealer info from notes
      const existingNotes = table.notes || '';
      const updatedNotes = existingNotes.replace(/\s*\|\s*Dealer:.*$/i, '').trim();
      
      return tablesAPI.updateTable(clubId, table.id, {
        notes: updatedNotes
      });
    },
    onSuccess: () => {
      toast.success('Dealer unassigned successfully');
      queryClient.invalidateQueries(['rummy-tables', clubId]);
      queryClient.invalidateQueries(['all-tables', clubId]);
      onClose(); // Close modal to refresh
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to unassign dealer');
    },
  });

  const handleUnassignDealer = () => {
    if (window.confirm(`Are you sure you want to unassign dealer "${dealerInfo.name}" from this table?`)) {
      unassignDealerMutation.mutate();
    }
  };
  
  // Fetch club data to get logo
  useEffect(() => {
    const fetchClubData = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        const tenantId = localStorage.getItem('tenantId');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3333/api'}/clubs/${clubId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-id': userId || '',
            'x-tenant-id': tenantId || '',
            'x-club-id': clubId,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setClubData(data);
          setCachedClubLogo(clubId, data?.logoUrl || null);
        }
      } catch (error) {
        console.error('Error fetching club data:', error);
      } finally {
        setClubFetched(true);
      }
    };
    if (clubId) {
      fetchClubData();
    }
  }, [clubId]);

  // Session timer — staff notes only (same as poker hologram; no seatedAt fallback).
  useEffect(() => {
    if (!table) {
      setSessionTime('00:00:00');
      return;
    }
    const tick = () => {
      setSessionTime(formatTableSessionHms(getTableSessionElapsedSecondsFromNotes(table.notes)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [table?.id, table?.notes]);

  const seats = Array.from({ length: table.maxSeats }, (_, i) => i + 1);

  // Get occupied seats from actual seated players data
  const occupiedSeats = seatedPlayersData.map(p => p.seatNumber).filter(Boolean);

  // Calculate table value from actual seated players' buy-ins
  const tableValue = seatedPlayersData.reduce((total, player) => {
    return total + (Number(player.buyInAmount) || 0);
  }, 0);

  const clubLogoUrl = clubData?.logoUrl || null;
  const gameType = table.rummyVariant || table.notes?.split('|')[1]?.trim() || 'RUMMY';
  const stakes = `₹${table.minBuyIn || 0}/₹${table.maxBuyIn || 0}`;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl p-8 max-w-4xl w-full border-2 border-emerald-500 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-white">Table {table.tableNumber} - {gameType}</h2>
            <p className="text-gray-400">Stakes: {stakes}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700 mb-6">
          <button
            onClick={() => setActiveTab('view')}
            className={`px-4 py-2 font-semibold transition-all ${
              activeTab === 'view'
                ? 'text-emerald-400 border-b-2 border-emerald-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Live View
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-semibold transition-all ${
              activeTab === 'history'
                ? 'text-emerald-400 border-b-2 border-emerald-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            History
          </button>
        </div>

        {activeTab === 'view' ? (
          <>
        {/* Table Value Display */}
        <div className="mb-6 flex justify-center">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 rounded-2xl shadow-xl border-2 border-emerald-400">
            <div className="text-xs text-emerald-100 text-center mb-1">TABLE VALUE</div>
            <div className="text-3xl font-bold text-white text-center">₹{tableValue.toLocaleString()}</div>
          </div>
        </div>

        {/* Rummy Table Visualization - Standard Round Shape */}
        <div className="relative w-full aspect-square max-w-[500px] mx-auto">
          {/* Outer Rail - Dark Wood */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-900 via-yellow-900 to-amber-950 shadow-2xl">
            {/* Padding Rail - Amber Cushion */}
            <div className="absolute inset-[10px] rounded-full bg-gradient-to-br from-amber-700 via-amber-600 to-amber-700">
              {/* Green Felt Surface */}
              <div className="absolute inset-[8px] rounded-full bg-gradient-to-br from-emerald-700 via-green-800 to-emerald-900 shadow-inner">
                {/* Subtle felt ring */}
                <div className="absolute inset-[30px] rounded-full border border-emerald-600/30"></div>
              </div>
            </div>
          </div>

          {/* Center Logo - Club Logo */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="w-24 h-24 bg-white/15 rounded-full border-2 border-white/25 flex items-center justify-center shadow-xl backdrop-blur-sm overflow-hidden">
              {clubLogoUrl ? (
                <>
                  <img 
                    src={clubLogoUrl} 
                    alt="Club Logo" 
                    className="w-full h-full object-contain rounded-full p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.logo-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="logo-fallback text-3xl font-bold text-white/40 hidden items-center justify-center w-full h-full">🎴</div>
                </>
              ) : clubFetched ? (
                <div className="text-3xl font-bold text-white/40">🎴</div>
              ) : null}
            </div>
          </div>

          {/* Dealer Position */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-20">
            {dealerInfo && (
              <div className="mb-2 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                <span>👤 {dealerInfo.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnassignDealer();
                  }}
                  disabled={unassignDealerMutation.isLoading}
                  className="hover:bg-purple-700 rounded-full p-0.5 transition-colors disabled:opacity-50"
                  title="Unassign Dealer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            <div className="bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-bold">
              D
            </div>
          </div>

          {/* Seats arranged in circle */}
          {seats.map((seatNum) => {
            const angle = (360 / table.maxSeats) * (seatNum - 1);
            const radians = (angle - 90) * (Math.PI / 180);
            const radius = 42;
            const x = 50 + radius * Math.cos(radians);
            const y = 50 + radius * Math.sin(radians);
            const isOccupied = occupiedSeats.includes(seatNum);
            const seatedPlayer = seatedPlayersData.find(p => p.seatNumber === seatNum);

            return (
              <div
                key={seatNum}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20"
                style={{ left: `${x}%`, top: `${y}%` }}
              >
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-lg transition-all cursor-pointer ${
                    isOccupied
                      ? 'bg-emerald-600 text-white border-2 border-emerald-400 shadow-lg shadow-emerald-500/30'
                      : 'bg-slate-700 text-gray-400 border-2 border-slate-600 hover:border-emerald-500'
                  }`}
                  title={seatedPlayer ? seatedPlayer.playerName : `Seat ${seatNum}`}
                >
                  {isOccupied && seatedPlayer ? (
                    seatedPlayer.playerName?.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase()
                  ) : (
                    seatNum
                  )}
                </div>
                <div className="text-center text-xs text-white mt-1 whitespace-nowrap max-w-[80px] overflow-hidden text-ellipsis">
                  {isOccupied && seatedPlayer ? seatedPlayer.playerName : `Seat ${seatNum}`}
                </div>
                {isOccupied && seatedPlayer && (() => {
                  const creditTbl = Math.max(0, Number(seatedPlayer.creditOnTableThisSession) || 0);
                  const cashTbl = Math.max(0, Number(seatedPlayer.cashOnTableThisSession) || 0);
                  const totalTbl = Math.max(0, Number(seatedPlayer.buyInAmount) || cashTbl + creditTbl);
                  return (
                    <div className="text-left text-[9px] leading-snug text-slate-100 bg-slate-900/95 px-2 py-1 rounded mt-0.5 w-[92px] tabular-nums">
                      <div className="text-emerald-200">Cash ₹{cashTbl.toLocaleString()}</div>
                      <div className="text-amber-200">Credit ₹{creditTbl.toLocaleString()}</div>
                      <div className="text-yellow-300 font-semibold border-t border-white/15 mt-0.5 pt-0.5">
                        Total ₹{totalTbl.toLocaleString()}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {/* Table Info - use real-time seated count from API */}
        <div className="mt-6 grid grid-cols-4 gap-4 bg-slate-800 p-4 rounded-lg">
          <div className="text-center">
            <div className="text-gray-400 text-sm">Players</div>
            <div className="text-white font-bold text-xl">{seatedPlayersData.length}/{table.maxSeats}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm">Table Value</div>
            <div className="text-emerald-400 font-bold text-xl">₹{tableValue.toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm">Buy-In Range</div>
            <div className="text-white font-bold text-lg">₹{(table.minBuyIn || 0).toLocaleString()} - ₹{(table.maxBuyIn || 0).toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm">Status</div>
            <div
              className={`font-bold text-xl ${
                isStaffSessionPausedNotes(table.notes) || table.status === 'PAUSED'
                  ? 'text-yellow-400'
                  : table.status === 'OCCUPIED' || (table.currentSeats || 0) > 0
                    ? 'text-green-400'
                    : 'text-gray-400'
              }`}
            >
              {isStaffSessionPausedNotes(table.notes) || table.status === 'PAUSED'
                ? 'Paused'
                : table.status === 'OCCUPIED' || (table.currentSeats || 0) > 0
                  ? 'Active'
                  : 'Waiting'}
            </div>
          </div>
        </div>

        {/* Session timer whenever staff session clock exists in notes */}
        {hasStaffTableSessionStarted(table.notes) && (
          <div
            className={`mt-4 p-4 rounded-lg text-center ${
              isStaffSessionPausedNotes(table.notes) || table.status === 'PAUSED'
                ? 'bg-gradient-to-r from-yellow-700 to-orange-700'
                : 'bg-gradient-to-r from-emerald-600 to-teal-600'
            }`}
          >
            <div className="text-white/70 text-xs mb-1 uppercase tracking-widest">
              {isStaffSessionPausedNotes(table.notes) || table.status === 'PAUSED'
                ? 'SESSION PAUSED (TIME)'
                : 'SESSION RUNNING'}
            </div>
            <div className="text-white font-bold text-3xl font-mono">{sessionTime}</div>
            {(isStaffSessionPausedNotes(table.notes) || table.status === 'PAUSED') && (
              <div className="mt-3 text-sm font-semibold text-amber-200">
                Session paused by staff
              </div>
            )}
          </div>
        )}

        {/* Collect Rake - Rummy allows multiple collections per session */}
        {showRakeForm ? (
          <div className="mt-6 p-4 bg-amber-900/30 border border-amber-500/50 rounded-lg">
            <h4 className="text-white font-semibold mb-3">Collect Rake – Table {table.tableNumber}</h4>
            <div className="grid grid-cols-1 gap-3 mb-3">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Amount (₹) *</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={rakeAmount}
                  onChange={(e) => setRakeAmount(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  placeholder="e.g. 500"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={rakeNotes}
                  onChange={(e) => setRakeNotes(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  placeholder="e.g. Round 1"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => rakeCollectionMutation.mutate({
                  tableId: table.id,
                  sessionDate: new Date().toISOString().split('T')[0],
                  totalRakeAmount: parseFloat(rakeAmount) || 0,
                  notes: rakeNotes || `Rake – Table ${table.tableNumber}`,
                })}
                disabled={rakeCollectionMutation.isPending || !rakeAmount || parseFloat(rakeAmount) <= 0}
                className="bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg"
              >
                {rakeCollectionMutation.isPending ? 'Saving...' : 'Collect Rake'}
              </button>
              <button
                onClick={() => { setShowRakeForm(false); setRakeAmount(''); setRakeNotes(''); }}
                className="bg-slate-600 hover:bg-slate-500 text-white font-semibold px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-3">
          {!showRakeForm && (
            <button
              onClick={() => setShowRakeForm(true)}
              className="bg-amber-600 hover:bg-amber-500 px-6 py-3 rounded-lg font-bold text-white transition-colors"
            >
              Collect Rake
            </button>
          )}
          <button
            onClick={onClose}
            className="bg-emerald-600 hover:bg-emerald-500 px-6 py-3 rounded-lg font-bold text-white transition-colors"
          >
            Close
          </button>
        </div>
          </>
        ) : (
          /* History Tab */
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white mb-4">Buy-In & Buy-Out History</h3>
            {tableHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No transaction history for this table yet.
              </div>
            ) : (
              <div className="space-y-3">
                {tableHistory.map((transaction, idx) => {
                  const isBuyIn = transaction.type === 'Table Buy In' || transaction.type === 'Buy In' || transaction.type === 'Club Buy In';
                  return (
                    <div 
                      key={transaction.id || idx} 
                      className={`p-4 rounded-lg border-2 ${
                        isBuyIn 
                          ? 'bg-green-900/20 border-green-600/50' 
                          : 'bg-orange-900/20 border-orange-600/50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">
                              {isBuyIn ? '💰' : '💵'}
                            </span>
                            <div>
                              <div className="font-bold text-white">
                                {transaction.playerName || transaction.player_name || 'Unknown Player'}
                              </div>
                              <div className="text-sm text-gray-400">
                                {isBuyIn ? 'Buy-In' : 'Buy-Out'}
                                {transaction.notes &&
                                  ` • ${sanitizeTransactionNotesForDisplay(transaction.notes)}`}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(transaction.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={`text-right ${isBuyIn ? 'text-green-400' : 'text-orange-400'}`}>
                          <div className="font-bold text-2xl">
                            {isBuyIn ? '+' : ''}₹{Number(transaction.amount).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-400">
                            {transaction.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="bg-emerald-600 hover:bg-emerald-500 px-6 py-3 rounded-lg font-bold text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Rummy Table Management View Component (CRUD with Rummy-specific fields)
// ============================================================================
function RummyTableManagementView({ selectedClubId, tables, tablesLoading, canManageTables = true }) {
  const [subTab, setSubTab] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [selectedTableForDealer, setSelectedTableForDealer] = useState("");
  const [selectedDealer, setSelectedDealer] = useState("");
  const queryClient = useQueryClient();

  // Fetch dealers (staff with 'Dealer' role - case sensitive!)
  const { data: staffData, isLoading: dealersLoading } = useQuery({
    queryKey: ['staff', selectedClubId],
    queryFn: () => staffAPI.getStaff(selectedClubId),
    enabled: !!selectedClubId,
  });

  // Filter to get all active dealers (role is 'Dealer' not 'DEALER')
  const allDealers = staffData?.filter(staff => staff.role === 'Dealer' && staff.status === 'Active') || [];

  // Get dealers on leave for today
  const today = new Date().toISOString().split('T')[0];
  const { data: dealersOnLeaveData } = useQuery({
    queryKey: ['dealersOnLeave', selectedClubId, today],
    queryFn: () => shiftsAPI.getDealers(selectedClubId, today),
    enabled: !!selectedClubId,
  });

  // Get dealers with shifts today (for assign dealer functionality)
  const { data: availableDealersForToday } = useQuery({
    queryKey: ['availableDealersForToday', selectedClubId, today],
    queryFn: () => shiftsAPI.getAvailableDealersForDate(selectedClubId, today),
    enabled: !!selectedClubId,
  });

  // Dealers on leave today (those NOT in the filtered list from API)
  // The API returns dealers NOT on leave, so dealers missing from that list are on leave
  const dealersNotOnLeave = dealersOnLeaveData?.dealers || [];
  const dealersOnLeaveIds = new Set(
    allDealers
      .filter(dealer => !dealersNotOnLeave.find(d => d.id === dealer.id))
      .map(d => d.id)
  );

  // Get dealers that are already assigned to active tables (check ALL tables in system)
  const { data: allTablesData } = useQuery({
    queryKey: ['all-tables', selectedClubId],
    queryFn: () => tablesAPI.getTables(selectedClubId),
    enabled: !!selectedClubId,
  });

  const assignedDealerIds = new Set(
    (allTablesData || [])
      .filter(table => (table.status === 'OCCUPIED' || table.status === 'RESERVED') && table.notes)
      .map(table => {
        const dealerMatch = table.notes?.match(/Dealer:\s*([a-f0-9-]{36})/i);
        return dealerMatch ? dealerMatch[1] : null;
      })
      .filter(Boolean)
  );

  // For assign dealer: Only show dealers who have shifts today AND are not on leave
  // Also filter to only rummy dealers (gameType is 'rummy' or not set)
  const dealersForAssignment = (availableDealersForToday?.dealers || []).filter(
    dealer => !assignedDealerIds.has(dealer.id) && dealer.gameType !== 'poker'
  );

  // For general display: Filter out dealers on leave, only show rummy dealers
  const availableDealers = allDealers.filter(
    dealer => !assignedDealerIds.has(dealer.id) && !dealersOnLeaveIds.has(dealer.id) && dealer.gameType !== 'poker'
  );

  // Assign dealer mutation
  const assignDealerMutation = useMutation({
    mutationFn: async ({ tableId, dealerId }) => {
      const table = tables.find(t => t.id === tableId);
      const dealer = allDealers.find(d => d.id === dealerId);
      
      if (!table || !dealer) {
        throw new Error('Table or dealer not found');
      }

      const existingNotes = table.notes || '';
      const notesWithoutDealer = existingNotes.replace(/\s*\|\s*Dealer:.*$/i, '');
      const updatedNotes = `${notesWithoutDealer} | Dealer: ${dealer.id} (${dealer.name})`.trim();

      return tablesAPI.updateTable(selectedClubId, tableId, {
        notes: updatedNotes
      });
    },
    onSuccess: () => {
      toast.success('Dealer assigned successfully');
      setSelectedTableForDealer("");
      setSelectedDealer("");
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
      queryClient.invalidateQueries(['all-tables', selectedClubId]);
      queryClient.invalidateQueries(['staff', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to assign dealer');
    },
  });

  const handleAssignDealer = () => {
    if (!selectedTableForDealer || !selectedDealer) {
      toast.error('Please select both table and dealer');
      return;
    }
    assignDealerMutation.mutate({
      tableId: selectedTableForDealer,
      dealerId: selectedDealer,
    });
  };

  // Rummy-specific variants
  const rummyVariants = [
    "Points Rummy",
    "Pool Rummy (101 Points)",
    "Pool Rummy (201 Points)",
    "Deals Rummy",
    "Custom",
  ];

  const [tableForm, setTableForm] = useState({
    tableNumber: "",
    tableName: "",
    rummy_variant: "Points Rummy",
    custom_variant: "",
    points_value: 1,
    number_of_deals: 1,
    drop_points: 20,
    max_points: 80,
    min_players: 2,
    max_players: 6,
    entry_fee: "",
    min_buy_in: "",
    max_buy_in: "",
    deal_duration: 5,
    notes: "",
  });

  // Create table mutation
  const createTableMutation = useMutation({
    mutationFn: (data) => tablesAPI.createTable(selectedClubId, {
      ...data,
      tableType: 'RUMMY',
    }),
    onSuccess: () => {
      toast.success('Rummy table created successfully');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
      setShowCreateModal(false);
      setSubTab("all");
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create rummy table');
    },
  });

  // Update table mutation
  const updateTableMutation = useMutation({
    mutationFn: ({ tableId, data }) => tablesAPI.updateTable(selectedClubId, tableId, {
      ...data,
      tableType: 'RUMMY',
    }),
    onSuccess: () => {
      toast.success('Rummy table updated successfully');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
      setShowCreateModal(false);
      setEditingTable(null);
      setSubTab("all");
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update rummy table');
    },
  });

  // Delete table mutation
  const deleteTableMutation = useMutation({
    mutationFn: (tableId) => tablesAPI.deleteTable(selectedClubId, tableId),
    onSuccess: () => {
      toast.success('Rummy table deleted successfully');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete rummy table');
    },
  });

  // Start session mutation
  const startSessionMutation = useMutation({
    mutationFn: (tableId) => tablesAPI.resumeSession(selectedClubId, tableId),
    onSuccess: () => {
      toast.success('Session started successfully');
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start session');
    },
  });

  const resetForm = () => {
    setTableForm({
      tableNumber: "",
      tableName: "",
      rummy_variant: "Points Rummy",
      custom_variant: "",
      points_value: 1,
      number_of_deals: 1,
      drop_points: 20,
      max_points: 80,
      min_players: 2,
      max_players: 6,
      entry_fee: "",
      min_buy_in: "",
      max_buy_in: "",
      deal_duration: 5,
      notes: "",
    });
    setEditingTable(null);
  };

  const handleCreateTable = (e) => {
    e.preventDefault();
    if (!selectedClubId) {
      toast.error('Please select a club first');
      return;
    }

    if (!tableForm.tableNumber) {
      toast.error('Table number is required');
      return;
    }

    if (editingTable) {
      updateTableMutation.mutate({
        tableId: editingTable.id,
        data: {
          tableType: 'RUMMY',
          maxSeats: parseInt(tableForm.max_players),
          minBuyIn: tableForm.min_buy_in ? parseFloat(tableForm.min_buy_in) : null,
          maxBuyIn: tableForm.max_buy_in ? parseFloat(tableForm.max_buy_in) : null,
          notes: tableForm.notes || `${tableForm.tableName || 'Rummy Table ' + tableForm.tableNumber} | ${tableForm.rummy_variant === 'Custom' ? tableForm.custom_variant : tableForm.rummy_variant} | Points: ${tableForm.points_value} | Deals: ${tableForm.number_of_deals} | Drop: ${tableForm.drop_points} | Max: ${tableForm.max_points}`,
          // Rummy-specific fields
          rummyVariant: tableForm.rummy_variant === 'Custom' ? tableForm.custom_variant : tableForm.rummy_variant,
          pointsValue: parseFloat(tableForm.points_value) || 1.0,
          numberOfDeals: parseInt(tableForm.number_of_deals) || null,
          dropPoints: parseInt(tableForm.drop_points) || null,
          maxPoints: parseInt(tableForm.max_points) || null,
          dealDuration: parseInt(tableForm.deal_duration) || null,
          entryFee: tableForm.entry_fee ? parseFloat(tableForm.entry_fee) : null,
          minPlayers: parseInt(tableForm.min_players) || 2,
        }
      });
    } else {
      createTableMutation.mutate({
        tableNumber: parseInt(tableForm.tableNumber),
        tableType: 'RUMMY',
        maxSeats: parseInt(tableForm.max_players),
        minBuyIn: tableForm.min_buy_in ? parseFloat(tableForm.min_buy_in) : null,
        maxBuyIn: tableForm.max_buy_in ? parseFloat(tableForm.max_buy_in) : null,
        notes: tableForm.notes || `${tableForm.tableName || 'Rummy Table ' + tableForm.tableNumber} | ${tableForm.rummy_variant === 'Custom' ? tableForm.custom_variant : tableForm.rummy_variant} | Points: ${tableForm.points_value} | Deals: ${tableForm.number_of_deals} | Drop: ${tableForm.drop_points} | Max: ${tableForm.max_points}`,
        // Rummy-specific fields
        rummyVariant: tableForm.rummy_variant === 'Custom' ? tableForm.custom_variant : tableForm.rummy_variant,
        pointsValue: parseFloat(tableForm.points_value) || 1.0,
        numberOfDeals: parseInt(tableForm.number_of_deals) || null,
        dropPoints: parseInt(tableForm.drop_points) || null,
        maxPoints: parseInt(tableForm.max_points) || null,
        dealDuration: parseInt(tableForm.deal_duration) || null,
        entryFee: tableForm.entry_fee ? parseFloat(tableForm.entry_fee) : null,
        minPlayers: parseInt(tableForm.min_players) || 2,
      });
    }
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setTableForm({
      tableNumber: table.tableNumber?.toString() || "",
      tableName: table.notes?.split('|')[0]?.trim() || "",
      rummy_variant: table.rummyVariant || "Points Rummy",
      custom_variant: "",
      points_value: table.pointsValue || 1,
      number_of_deals: table.numberOfDeals || 1,
      drop_points: table.dropPoints || 20,
      max_points: table.maxPoints || 80,
      min_players: table.minPlayers || 2,
      max_players: table.maxSeats || 6,
      entry_fee: table.entryFee?.toString() || "",
      min_buy_in: table.minBuyIn?.toString() || "",
      max_buy_in: table.maxBuyIn?.toString() || "",
      deal_duration: table.dealDuration || 5,
      notes: table.notes || "",
    });
    setSubTab("add");
  };

  const handleDeleteTable = (table) => {
    if (window.confirm(`Are you sure you want to delete Table ${table.tableNumber}?`)) {
      deleteTableMutation.mutate(table.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold mb-6">Rummy Table Management</h2>

        {/* Sub Tabs */}
        <div className="flex gap-2 border-b border-slate-700 pb-4 mb-6">
          <button
            onClick={() => setSubTab("all")}
            className={`px-5 py-2 rounded-t-lg font-semibold transition-all ${
              subTab === "all"
                ? "bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-lg"
                : "bg-slate-700 text-gray-400 hover:bg-slate-600"
            }`}
          >
            All Tables
          </button>
          {canManageTables && (
            <button
              onClick={() => {
                setSubTab("add");
                setEditingTable(null);
                resetForm();
              }}
              className={`px-5 py-2 rounded-t-lg font-semibold transition-all ${
                subTab === "add"
                  ? "bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-lg"
                  : "bg-slate-700 text-gray-400 hover:bg-slate-600"
              }`}
            >
              Add New Table
            </button>
          )}
        </div>

        {/* All Tables List */}
        {subTab === "all" && (
          <div className="space-y-4">
            {tablesLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                <p>Loading rummy tables...</p>
              </div>
            ) : tables.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🃏</div>
                <p className="text-xl text-gray-300">No rummy tables created yet</p>
                <p className="text-gray-400 text-sm mt-2">Click "Add New Table" to create your first rummy table</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tables.map((table) => {
                  const notes = String(table.notes || '');
                  const hasSessionMarker =
                    /Session Started:/i.test(notes) ||
                    /Paused Elapsed:/i.test(notes);
                  const sessionRunning =
                    hasSessionMarker ||
                    (Number(table.currentSeats || 0) > 0) ||
                    ['OCCUPIED', 'RESERVED', 'PAUSED'].includes(String(table.status || '').toUpperCase());

                  return (
                  <div
                    key={table.id}
                    className="bg-slate-700 rounded-xl p-5 border border-slate-600"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-white">Table {table.tableNumber}</h3>
                        <p className="text-sm text-gray-400">{table.rummyVariant || 'RUMMY'}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        table.status === 'AVAILABLE' ? 'bg-green-600/20 text-green-400' :
                        table.status === 'OCCUPIED' ? 'bg-emerald-600/20 text-emerald-400' :
                        table.status === 'RESERVED' ? 'bg-yellow-600/20 text-yellow-400' :
                        'bg-gray-600/20 text-gray-400'
                      }`}>
                        {table.status}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Max Seats:</span>
                        <span className="text-white font-medium">{table.maxSeats}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Stakes:</span>
                        <span className="text-white font-medium">₹{table.minBuyIn || 0}/₹{table.maxBuyIn || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Current Seats:</span>
                        <span className="text-white font-medium">{table.currentSeats || 0} / {table.maxSeats}</span>
                      </div>
                    </div>

                    {!sessionRunning && canManageTables ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startSessionMutation.mutate(table.id)}
                          disabled={startSessionMutation.isLoading}
                          className="flex-1 bg-green-600 hover:bg-green-500 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          <span>▶</span>
                          <span>{startSessionMutation.isLoading ? 'Starting…' : 'Start Session'}</span>
                        </button>
                        <button
                          onClick={() => handleEditTable(table)}
                          className="flex-1 bg-blue-600 hover:bg-blue-500 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTable(table)}
                          disabled={deleteTableMutation.isLoading}
                          className="flex-1 bg-red-600 hover:bg-red-500 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                        {sessionRunning
                          ? 'Session running - manage this table from Live Rummy Tables / Session Control.'
                          : 'View-only access for this role.'}
                      </div>
                    )}
                  </div>
                )})}
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Table Form */}
        {subTab === "add" && canManageTables && (
          <div className="bg-slate-700 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-6">{editingTable ? 'Edit Rummy Table' : 'Create New Rummy Table'}</h3>
            <form onSubmit={handleCreateTable} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Table Number *</label>
                  <input
                    type="number"
                    value={tableForm.tableNumber}
                    onChange={(e) => setTableForm({...tableForm, tableNumber: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="1"
                    required
                    disabled={!!editingTable}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Table Name</label>
                  <input
                    type="text"
                    value={tableForm.tableName}
                    onChange={(e) => setTableForm({...tableForm, tableName: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="e.g., Premium Rummy Table"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Rummy Variant *</label>
                <select
                  value={tableForm.rummy_variant}
                  onChange={(e) => setTableForm({...tableForm, rummy_variant: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  required
                >
                  {rummyVariants.map(variant => (
                    <option key={variant} value={variant}>{variant}</option>
                  ))}
                </select>
              </div>

              {tableForm.rummy_variant === 'Custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Custom Variant Name</label>
                  <input
                    type="text"
                    value={tableForm.custom_variant}
                    onChange={(e) => setTableForm({...tableForm, custom_variant: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Enter custom variant name"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Points Value (per ₹)</label>
                <input
                  type="number"
                  value={tableForm.points_value}
                  onChange={(e) => setTableForm({...tableForm, points_value: parseFloat(e.target.value)})}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  min="0.1"
                  step="0.1"
                />
              </div>

              {(tableForm.rummy_variant.includes('Points') || tableForm.rummy_variant === 'Custom') && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Drop Points</label>
                      <input
                        type="number"
                        value={tableForm.drop_points}
                        onChange={(e) => setTableForm({...tableForm, drop_points: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Max Points</label>
                      <input
                        type="number"
                        value={tableForm.max_points}
                        onChange={(e) => setTableForm({...tableForm, max_points: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        min="0"
                      />
                    </div>
                  </div>
                </>
              )}

              {tableForm.rummy_variant === 'Deals Rummy' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Number of Deals</label>
                  <input
                    type="number"
                    value={tableForm.number_of_deals}
                    onChange={(e) => setTableForm({...tableForm, number_of_deals: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    min="1"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Min Players</label>
                  <input
                    type="number"
                    value={tableForm.min_players}
                    onChange={(e) => setTableForm({...tableForm, min_players: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    min="2"
                    max="6"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Max Players *</label>
                  <input
                    type="number"
                    value={tableForm.max_players}
                    onChange={(e) => setTableForm({...tableForm, max_players: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    min="2"
                    max="6"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Entry Fee (₹)</label>
                  <input
                    type="number"
                    value={tableForm.entry_fee}
                    onChange={(e) => setTableForm({...tableForm, entry_fee: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Min Buy-in (₹)</label>
                  <input
                    type="number"
                    value={tableForm.min_buy_in}
                    onChange={(e) => setTableForm({...tableForm, min_buy_in: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Max Buy-in (₹)</label>
                  <input
                    type="number"
                    value={tableForm.max_buy_in}
                    onChange={(e) => setTableForm({...tableForm, max_buy_in: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Deal Duration (minutes)</label>
                <input
                  type="number"
                  value={tableForm.deal_duration}
                  onChange={(e) => setTableForm({...tableForm, deal_duration: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea
                  value={tableForm.notes}
                  onChange={(e) => setTableForm({...tableForm, notes: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  rows="3"
                  placeholder="Additional notes about this table..."
                />
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  type="submit"
                  disabled={createTableMutation.isLoading || updateTableMutation.isLoading || !selectedClubId}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {createTableMutation.isLoading || updateTableMutation.isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {editingTable ? 'Updating...' : 'Creating...'}
                    </span>
                  ) : (
                    editingTable ? 'Update Table' : 'Create Table'
                  )}
                </button>
                {editingTable && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTable(null);
                      resetForm();
                      setSubTab("all");
                    }}
                    className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 rounded-lg font-semibold transition-all"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Dealer Assignment Section */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-xl font-bold mb-4">Dealer Assignment</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Select Table</label>
            <select 
              value={selectedTableForDealer}
              onChange={(e) => setSelectedTableForDealer(e.target.value)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              disabled={!selectedClubId}
            >
              <option value="">-- Select Table --</option>
              {tables.map((table) => (
                <option key={table.id} value={table.id}>
                  Table {table.tableNumber} - {table.rummyVariant || 'Rummy'} ({table.status})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Assign Dealer</label>
            <select 
              value={selectedDealer}
              onChange={(e) => setSelectedDealer(e.target.value)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              disabled={!selectedClubId || dealersLoading}
            >
              <option value="">-- Select Dealer --</option>
              {dealersLoading ? (
                <option disabled>Loading dealers...</option>
              ) : dealersForAssignment.length === 0 ? (
                <option disabled>
                  {allDealers.length > 0 
                    ? 'No dealers available (all assigned, on leave, or no shift today)' 
                    : 'No active dealers found'}
                </option>
              ) : (
                dealersForAssignment.map((dealer) => (
                  <option key={dealer.id} value={dealer.id}>
                    {dealer.name} ({dealer.employeeId || 'No ID'})
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
        <button 
          onClick={handleAssignDealer}
          disabled={!selectedTableForDealer || !selectedDealer || assignDealerMutation.isLoading || !selectedClubId}
          className="mt-4 bg-purple-600 hover:bg-purple-500 px-6 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {assignDealerMutation.isLoading ? 'Assigning...' : 'Assign Dealer'}
        </button>
        {allDealers.length === 0 && !dealersLoading && selectedClubId && (
          <p className="mt-2 text-sm text-yellow-400">
            ⚠️ No active dealers found. Please create dealer staff members first.
          </p>
        )}
        {dealersForAssignment.length === 0 && allDealers.length > 0 && !dealersLoading && (
          <p className="mt-2 text-sm text-amber-400">
            ℹ️ No dealers available. They may be assigned, on leave, or don't have a shift today.
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Table Buy-In View Component (Waitlist Management)
// ============================================================================
function TableBuyInView({ selectedClubId, tables, waitlist, waitlistLoading, canAssignSeat = true }) {
  const queryClient = useQueryClient();
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedSeat, setSelectedSeat] = useState("");
  const [selectedTableForView, setSelectedTableForView] = useState(null); // { table, waitlistEntry }
  const [showFullCurrentWaitlist, setShowFullCurrentWaitlist] = useState(false);
  const [showFullReorderWaitlist, setShowFullReorderWaitlist] = useState(false);

  // Remove waitlist entry
  const removeWaitlistMutation = useMutation({
    mutationFn: (entryId) => waitlistAPI.deleteWaitlistEntry(selectedClubId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries(['waitlist', selectedClubId]);
      toast.success('Player removed from waitlist');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove from waitlist');
    },
  });

  // Assign seat
  const assignSeatMutation = useMutation({
    mutationFn: ({ entryId, tableId, seatNumber }) =>
      waitlistAPI.assignSeat(selectedClubId, entryId, { tableId, seatedBy: 'Super Admin', seatNumber: seatNumber != null ? parseInt(seatNumber, 10) : undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries(['waitlist', selectedClubId]);
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
      setSelectedPlayer("");
      setSelectedTable("");
      setSelectedSeat("");
      toast.success('Seat assigned successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to assign seat');
    },
  });

  // Reorder waitlist
  const reorderWaitlistMutation = useMutation({
    mutationFn: ({ entryId, newPriority }) =>
      waitlistAPI.updateWaitlistEntry(selectedClubId, entryId, { priority: newPriority }),
    onSuccess: () => {
      queryClient.invalidateQueries(['waitlist', selectedClubId]);
      toast.success('Waitlist reordered');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reorder waitlist');
    },
  });

  // Sort pending waitlist entries by priority (desc) then createdAt (asc)
  // Rummy section: only show waitlist entries that requested rummy (case-insensitive; legacy null = poker, shown on Poker only)
  const pendingWaitlist = (Array.isArray(waitlist) ? waitlist : [])
    .filter((e) => e.status === 'PENDING' && String(e.requestedGameType || '').toUpperCase() === 'RUMMY')
    .sort((a, b) => {
      const pa = a.priority || 0, pb = b.priority || 0;
      if (pb !== pa) return pb - pa;
      return new Date(a.createdAt) - new Date(b.createdAt);
    })
    .map((entry, index) => ({ ...entry, position: index + 1 }));

  const handleViewTable = (entry) => {
    // Try to match by tableType (RUMMY), fall back to any available table
    let table = tables.find((t) => t.tableType === 'RUMMY' && t.status === 'AVAILABLE');
    if (!table) table = tables.find((t) => t.status === 'AVAILABLE');
    if (!table && tables.length > 0) table = tables[0];
    if (table) {
      setSelectedTableForView({ table, waitlistEntry: entry });
    } else {
      toast.error('No rummy tables available. Please create a rummy table first.');
    }
  };

  const handleAssignSeat = () => {
    if (!selectedPlayer || !selectedTable || !selectedSeat) {
      toast.error('Please select player, table, and seat');
      return;
    }
    assignSeatMutation.mutate({ entryId: selectedPlayer, tableId: selectedTable, seatNumber: selectedSeat ? parseInt(selectedSeat, 10) : undefined });
  };

  // Find max seats for selected table (for seat dropdown)
  const selectedTableObj = tables.find((t) => t.id === selectedTable);
  const maxSeatsForSelected = selectedTableObj?.maxSeats || 6;

  return (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold mb-6">Waitlist Management</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Waitlist */}
          <div className="bg-slate-700 rounded-xl p-5 min-h-[420px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Current Waitlist</h3>
              {pendingWaitlist.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowFullCurrentWaitlist(true)}
                  className="text-xs px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500 transition-colors"
                >
                  View Full List ({pendingWaitlist.length})
                </button>
              )}
            </div>
            {waitlistLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-2"></div>
                <p className="text-sm">Loading...</p>
              </div>
            ) : pendingWaitlist.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No players in waitlist</div>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-y-auto">
                {pendingWaitlist.map((entry) => (
                  <div key={entry.id} className="bg-slate-600 p-4 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-white">{entry.playerName}</div>
                        <div className="text-sm text-gray-400">Position: #{entry.position}</div>
                        <div className="text-sm text-gray-400">Table Type: {entry.tableType || 'Rummy'}</div>
                        {entry.requestedSeat && (
                          <div className="text-sm text-rose-400">Requested Seat: {entry.requestedSeat}</div>
                        )}
                      </div>
                      <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 rounded text-xs font-semibold">
                        {entry.status}
                      </span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleViewTable(entry)}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 rounded text-sm font-medium transition-colors"
                      >
                        🎯 View Table
                      </button>
                      <button
                        onClick={() => removeWaitlistMutation.mutate(entry.id)}
                        disabled={removeWaitlistMutation.isLoading}
                        className="flex-1 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-3 py-1.5 rounded text-sm font-medium transition-colors"
                      >
                        {removeWaitlistMutation.isLoading ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Seat Allocation */}
          <div className="bg-slate-700 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4">Seat Allocation</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Select Player</label>
                <select
                  value={selectedPlayer}
                  onChange={(e) => {
                    setSelectedPlayer(e.target.value);
                    // Auto-select first available rummy table
                    const matchingTable = tables.find((t) => t.tableType === 'RUMMY');
                    if (matchingTable) setSelectedTable(matchingTable.id);
                    setSelectedSeat('');
                  }}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                >
                  <option value="">-- Select Player --</option>
                  {pendingWaitlist.map((entry) => (
                    <option key={entry.id} value={entry.id}>{entry.playerName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Select Table</label>
                <select
                  value={selectedTable}
                  onChange={(e) => { setSelectedTable(e.target.value); setSelectedSeat(''); }}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                >
                  <option value="">-- Select Table --</option>
                  {tables.map((table) => (
                    <option key={table.id} value={table.id}>
                      Table {table.tableNumber} ({table.currentSeats || 0}/{table.maxSeats} seats)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Seat Number</label>
                <select
                  value={selectedSeat}
                  onChange={(e) => setSelectedSeat(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                >
                  <option value="">-- Select Seat --</option>
                  {Array.from({ length: maxSeatsForSelected }, (_, i) => i + 1).map((seat) => (
                    <option key={seat} value={seat}>Seat {seat}</option>
                  ))}
                </select>
              </div>
              {canAssignSeat && (
                <button
                  onClick={handleAssignSeat}
                  disabled={!selectedPlayer || !selectedTable || !selectedSeat || assignSeatMutation.isLoading}
                  className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-3 rounded-lg font-semibold transition-colors"
                >
                  {assignSeatMutation.isLoading ? 'Assigning…' : 'Assign Seat'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Player Call & Reorder */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold mb-6">Player Call & Reorder</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Call Players */}
          <div className="bg-slate-700 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4">Call Players</h3>
            <div className="space-y-3">
              <button
                onClick={() => {
                  const next = pendingWaitlist[0];
                  if (!next) { toast.error('No players in waitlist'); return; }
                  toast.success(`Called ${next.playerName} — Position #1`);
                }}
                className="w-full bg-green-600 hover:bg-green-500 px-4 py-3 rounded-lg font-semibold transition-colors"
              >
                Call Next Player
              </button>
              <button
                onClick={() => {
                  if (pendingWaitlist.length === 0) { toast.error('No players in waitlist'); return; }
                  toast.success(`Called all ${pendingWaitlist.length} players`);
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-500 px-4 py-3 rounded-lg font-semibold transition-colors"
              >
                Call All Players
              </button>
              <button
                onClick={() => toast('SMS feature coming soon')}
                className="w-full bg-yellow-600 hover:bg-yellow-500 px-4 py-3 rounded-lg font-semibold transition-colors"
              >
                Send SMS Notification
              </button>
            </div>
          </div>

          {/* Reorder Waitlist */}
          <div className="bg-slate-700 rounded-xl p-5 min-h-[420px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Reorder Waitlist</h3>
              {pendingWaitlist.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowFullReorderWaitlist(true)}
                  className="text-xs px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500 transition-colors"
                >
                  Expand
                </button>
              )}
            </div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {pendingWaitlist.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No players to reorder</div>
              ) : (
                pendingWaitlist.map((entry, index) => (
                  <div key={entry.id} className="bg-slate-600 p-3 rounded flex justify-between items-center">
                    <span className="text-white">{index + 1}. {entry.playerName}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => reorderWaitlistMutation.mutate({ entryId: entry.id, newPriority: (entry.priority || 0) + 1 })}
                        disabled={index === 0 || reorderWaitlistMutation.isLoading}
                        className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-2 py-1 rounded text-xs transition-colors"
                      >↑</button>
                      <button
                        onClick={() => reorderWaitlistMutation.mutate({ entryId: entry.id, newPriority: (entry.priority || 0) - 1 })}
                        disabled={index === pendingWaitlist.length - 1 || reorderWaitlistMutation.isLoading}
                        className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-2 py-1 rounded text-xs transition-colors"
                      >↓</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Current Waitlist Modal */}
      {showFullCurrentWaitlist && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Current Waitlist ({pendingWaitlist.length})</h2>
              <button
                type="button"
                onClick={() => setShowFullCurrentWaitlist(false)}
                className="px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500"
              >
                Close
              </button>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              {pendingWaitlist.map((entry) => (
                <div key={entry.id} className="bg-slate-700 p-4 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-white">{entry.playerName}</div>
                      <div className="text-sm text-gray-400">Position: #{entry.position || 'N/A'}</div>
                      <div className="text-sm text-gray-400">Table Type: {entry.tableType || 'Rummy'}</div>
                      <div className="text-sm text-rose-400">Requested Seat: {entry.requestedSeat || 'Any'}</div>
                    </div>
                    <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 rounded text-xs font-semibold">
                      {entry.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Full Reorder Waitlist Modal */}
      {showFullReorderWaitlist && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-auto border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Reorder Waitlist ({pendingWaitlist.length})</h2>
              <button
                type="button"
                onClick={() => setShowFullReorderWaitlist(false)}
                className="px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500"
              >
                Close
              </button>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {pendingWaitlist.map((entry, index) => (
                <div key={entry.id} className="bg-slate-700 p-3 rounded flex justify-between items-center">
                  <span className="text-white">{index + 1}. {entry.playerName}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => reorderWaitlistMutation.mutate({ entryId: entry.id, newPriority: (entry.priority || 0) + 1 })}
                      disabled={index === 0 || reorderWaitlistMutation.isLoading}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-2 py-1 rounded text-xs transition-colors"
                    >↑</button>
                    <button
                      onClick={() => reorderWaitlistMutation.mutate({ entryId: entry.id, newPriority: (entry.priority || 0) - 1 })}
                      disabled={index === pendingWaitlist.length - 1 || reorderWaitlistMutation.isLoading}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-2 py-1 rounded text-xs transition-colors"
                    >↓</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* View Table & Seat Assignment Modal */}
      {selectedTableForView && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                🃏 Assign Player to Rummy Table {selectedTableForView.table.tableNumber}
              </h2>
              <button
                onClick={() => { setSelectedTableForView(null); setSelectedSeat(''); }}
                className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded transition-colors"
              >
                Close
              </button>
            </div>

            {/* Round Rummy Table Visualization */}
            <div className="flex justify-center mb-6">
              <div className="relative w-64 h-64 sm:w-72 sm:h-72">
                {/* Outer rail */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-900 via-yellow-900 to-amber-950 shadow-2xl">
                  {/* Cushion */}
                  <div className="absolute inset-[8px] rounded-full bg-gradient-to-br from-rose-700 via-red-600 to-rose-700">
                    {/* Felt */}
                    <div className="absolute inset-[6px] rounded-full bg-gradient-to-br from-rose-900 via-rose-800 to-rose-950 shadow-inner">
                      {/* Subtle inner ring */}
                      <div className="absolute inset-[20px] rounded-full border border-rose-600/30"></div>

                      {/* Seat positions arranged in circle */}
                      {Array.from({ length: selectedTableForView.table.maxSeats || 6 }, (_, i) => {
                        const total = selectedTableForView.table.maxSeats || 6;
                        const angle = (i * (2 * Math.PI / total)) - Math.PI / 2;
                        const r = 40;
                        const x = 50 + r * Math.cos(angle);
                        const y = 50 + r * Math.sin(angle);
                        const isWaiting = selectedTableForView.waitlistEntry?.requestedSeat === (i + 1);
                        return (
                          <div
                            key={i}
                            className="absolute transform -translate-x-1/2 -translate-y-1/2"
                            style={{ left: `${x}%`, top: `${y}%` }}
                          >
                            <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center shadow-lg ${isWaiting ? 'bg-yellow-500 border-yellow-300 animate-pulse' : 'bg-slate-700/80 border-slate-500'}`}>
                              <span className="text-white text-xs font-bold">{i + 1}</span>
                            </div>
                          </div>
                        );
                      })}

                      {/* Center */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-2xl">🃏</div>
                          <div className="text-white text-xs font-bold mt-1">
                            {selectedTableForView.table.rummyVariant || 'Rummy'}
                          </div>
                          <div className="text-rose-300 text-[10px] mt-0.5">
                            {selectedTableForView.table.currentSeats || 0}/{selectedTableForView.table.maxSeats} players
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table Info */}
            <div className="bg-slate-700 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-lg mb-2">Table Information</h3>
              <div className="grid grid-cols-2 gap-3 text-gray-300 text-sm">
                <div><span className="text-gray-400">Type:</span> {selectedTableForView.table.tableType}</div>
                <div><span className="text-gray-400">Seats:</span> {selectedTableForView.table.currentSeats}/{selectedTableForView.table.maxSeats}</div>
                <div><span className="text-gray-400">Status:</span>
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs ${selectedTableForView.table.status === 'AVAILABLE' ? 'bg-green-600' : selectedTableForView.table.status === 'OCCUPIED' ? 'bg-yellow-600' : 'bg-gray-600'}`}>
                    {selectedTableForView.table.status}
                  </span>
                </div>
                <div><span className="text-gray-400">Available:</span> {(selectedTableForView.table.maxSeats || 6) - (selectedTableForView.table.currentSeats || 0)} seats</div>
                {selectedTableForView.table.entryFee && <div><span className="text-gray-400">Entry Fee:</span> ₹{selectedTableForView.table.entryFee}</div>}
                {selectedTableForView.table.pointsValue && <div><span className="text-gray-400">Points Value:</span> ₹{selectedTableForView.table.pointsValue}/pt</div>}
              </div>
            </div>

            {/* Player Info */}
            <div className="bg-slate-700 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-lg mb-2">Player Waiting</h3>
              <div className="grid grid-cols-2 gap-3 text-gray-300 text-sm">
                <div><span className="text-gray-400">Name:</span> {selectedTableForView.waitlistEntry.playerName}</div>
                <div><span className="text-gray-400">Position:</span> #{selectedTableForView.waitlistEntry.position || 'N/A'}</div>
                <div><span className="text-gray-400">Requested Seat:</span> {selectedTableForView.waitlistEntry.requestedSeat || 'Any'}</div>
                <div><span className="text-gray-400">Party Size:</span> {selectedTableForView.waitlistEntry.partySize || 1}</div>
              </div>
            </div>

            {/* Seat Assignment */}
            <div className="bg-slate-700 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-4">Assign Seat</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Select Seat Number</label>
                  <select
                    value={selectedSeat}
                    onChange={(e) => setSelectedSeat(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  >
                    <option value="">-- Choose a seat --</option>
                    {Array.from({ length: selectedTableForView.table.maxSeats || 6 }, (_, i) => i + 1).map((seatNum) => (
                      <option key={seatNum} value={seatNum}>
                        Seat {seatNum}{selectedTableForView.waitlistEntry.requestedSeat === seatNum ? ' (Requested)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {canAssignSeat ? (
                  <button
                    onClick={() => {
                      if (!selectedSeat) { toast.error('Please select a seat number'); return; }
                      assignSeatMutation.mutate({
                        entryId: selectedTableForView.waitlistEntry.id,
                        tableId: selectedTableForView.table.id,
                        seatNumber: parseInt(selectedSeat, 10)
                      });
                      setSelectedTableForView(null);
                      setSelectedSeat('');
                    }}
                    disabled={!selectedSeat || assignSeatMutation.isLoading}
                    className="w-full bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    {assignSeatMutation.isLoading ? 'Assigning…' : `Assign to Seat ${selectedSeat || '…'}`}
                  </button>
                ) : (
                  <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    View-only access for this role.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Table Buy-Out View Component
// ============================================================================
function TableBuyOutView({ selectedClubId, tables }) {
  const [activeSubTab, setActiveSubTab] = useState("process-buyout");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedTableForBuyOut, setSelectedTableForBuyOut] = useState("");
  const [buyOutReason, setBuyOutReason] = useState("");
  const [buyOutAmount, setBuyOutAmount] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const rummyTablesWithSeats = (tables || []).filter((t) => (t.currentSeats || 0) > 0);
  const seatedCountAllRummy = rummyTablesWithSeats.reduce((n, t) => n + (t.currentSeats || 0), 0);

  const { data: seatedPlayersForTable = [], isLoading: seatedPlayersLoading } = useQuery({
    queryKey: ['seatedPlayersForTable', 'rummy', selectedClubId, selectedTableForBuyOut],
    queryFn: async () => {
      if (!selectedClubId || !selectedTableForBuyOut) return [];
      const response = await tablesAPI.getSeatedPlayersForTable(selectedClubId, selectedTableForBuyOut);
      return response?.seatedPlayers || [];
    },
    enabled: !!selectedClubId && !!selectedTableForBuyOut && activeSubTab === 'process-buyout',
  });

  const handleApproveBuyOut = async () => {
    if (!selectedPlayer) {
      toast.error('Please select a player');
      return;
    }
    const raw = String(buyOutAmount ?? '').trim();
    if (raw === '') {
      toast.error('Enter buy-out amount (use 0 if no chips to return)');
      return;
    }
    const amount = parseFloat(raw);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount (0 or greater)');
      return;
    }

    const confirmMsg =
      amount === 0
        ? `Confirm: Complete buy-out for ${selectedPlayer.playerName} with ₹0 chip return to wallet? Credit owed will still be settled from the wallet if applicable.`
        : `Confirm: Add ₹${amount.toLocaleString('en-IN')} to ${selectedPlayer.playerName}'s balance and complete buy-out?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const tableRow = tables.find((t) => t.id === selectedTableForBuyOut);
      const tableNumber = tableRow?.tableNumber;
      if (tableNumber == null || Number.isNaN(Number(tableNumber))) {
        toast.error('Could not resolve table number for buy-out');
        return;
      }

      const result = await superAdminAPI.manualTableBuyOut(selectedClubId, {
        playerId: selectedPlayer.playerId,
        tableNumber: Number(tableNumber),
        amount,
        reason: buyOutReason || undefined,
      });

      const creditSettled = result?.creditSettled ?? 0;
      toast.success(
        creditSettled > 0
          ? `Buy-out complete for ${selectedPlayer.playerName}: ₹${amount.toLocaleString('en-IN')} chips to wallet; ₹${Number(creditSettled).toLocaleString('en-IN')} credit settled (wallet may show negative until repaid).`
          : `Buy-out complete for ${selectedPlayer.playerName}: ₹${amount.toLocaleString('en-IN')} returned to wallet.`,
      );

      setSelectedPlayer(null);
      setBuyOutAmount('');
      setBuyOutReason('');

      queryClient.invalidateQueries({ queryKey: ['seatedPlayersForTable'] });
      queryClient.invalidateQueries({ queryKey: ['tables', selectedClubId] });
      queryClient.invalidateQueries({ queryKey: ['rummy-tables', selectedClubId] });
      queryClient.invalidateQueries({ queryKey: ['waitlist', selectedClubId] });
    } catch (error) {
      console.error('Error approving buy-out:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to approve buy-out');
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['seatedPlayersForTable'] }),
        queryClient.refetchQueries({ queryKey: ['tables', selectedClubId] }),
        queryClient.refetchQueries({ queryKey: ['rummy-tables', selectedClubId] }),
        queryClient.refetchQueries({ queryKey: ['waitlist', selectedClubId] }),
      ]);
      toast.success('Rummy buy-out data refreshed');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/30 via-red-500/20 to-rose-700/30 rounded-xl p-6 border border-orange-800/40">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Rummy Table Buy-Out</h2>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors"
          >
            {isRefreshing ? (
              <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <p className="text-gray-300 text-sm mb-6">
          Same flow as poker: pick a rummy table, select a seated player, enter chip total to return to wallet, then approve.
        </p>

        <div className="flex gap-2 border-b border-orange-800/40 pb-4 mb-6">
          <button
            type="button"
            onClick={() => setActiveSubTab("process-buyout")}
            className={`px-5 py-2 rounded-t-lg font-semibold transition-all ${
              activeSubTab === "process-buyout"
                ? "bg-gradient-to-r from-orange-600 to-red-700 text-white shadow-lg"
                : "bg-slate-700 text-gray-400 hover:bg-slate-600"
            }`}
          >
            Process Buy-Out
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab("player-requests")}
            className={`px-5 py-2 rounded-t-lg font-semibold transition-all ${
              activeSubTab === "player-requests"
                ? "bg-gradient-to-r from-orange-600 to-red-700 text-white shadow-lg"
                : "bg-slate-700 text-gray-400 hover:bg-slate-600"
            }`}
          >
            Player Requests
          </button>
        </div>

        {activeSubTab === "process-buyout" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white/10 p-5 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Process Buy-Out</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">1. Select Table</label>
                  <select
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    value={selectedTableForBuyOut}
                    onChange={(e) => {
                      setSelectedTableForBuyOut(e.target.value);
                      setSelectedPlayer(null);
                    }}
                  >
                    <option value="">-- Select Table --</option>
                    {rummyTablesWithSeats.map((table) => (
                      <option key={table.id} value={table.id}>
                        Table {table.tableNumber} ({table.currentSeats || 0} players)
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTableForBuyOut && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">2. Select Seated Player</label>
                    <select
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      value={selectedPlayer?.playerId || ''}
                      onChange={(e) => {
                        const player = seatedPlayersForTable.find((p) => p.playerId === e.target.value);
                        setSelectedPlayer(player || null);
                        if (player && (Number(player.buyInAmount) || 0) > 0) {
                          setBuyOutAmount(String(Number(player.buyInAmount)));
                        }
                      }}
                      disabled={seatedPlayersLoading || seatedPlayersForTable.length === 0}
                    >
                      <option value="">
                        {seatedPlayersLoading ? 'Loading...' : seatedPlayersForTable.length === 0 ? 'No seated players' : '-- Select Player --'}
                      </option>
                      {seatedPlayersForTable.map((player) => (
                        <option key={player.playerId} value={player.playerId}>
                          {player.playerName} - Seat #{player.seatNumber}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedPlayer && (
                  <>
                    <div className="p-4 bg-blue-500/20 rounded-lg border border-blue-400/30">
                      <div className="text-sm text-white space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Player:</span>
                          <span className="font-semibold">{selectedPlayer.playerName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Seat:</span>
                          <span className="font-semibold">#{selectedPlayer.seatNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Cash (on table):</span>
                          <span className="font-semibold text-emerald-200">
                            ₹{(Number(selectedPlayer.cashOnTableThisSession) || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Credit (on table):</span>
                          <span className="font-semibold text-amber-200">
                            ₹{(Number(selectedPlayer.creditOnTableThisSession) || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-white/10 pt-2">
                          <span className="text-gray-300">Total (chips):</span>
                          <span className="font-semibold text-cyan-300">
                            ₹{(Number(selectedPlayer.buyInAmount) || 0).toLocaleString()}
                          </span>
                        </div>
                        {selectedPlayer.creditFacilityEnabled && Number(selectedPlayer.creditLineLimit) > 0 ? (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-300">Total credit line:</span>
                              <span className="font-semibold text-sky-200">
                                ₹{(Number(selectedPlayer.creditLineLimit) || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-300">Credit used:</span>
                              <span className="font-semibold text-rose-300">
                                ₹{(Number(selectedPlayer.creditLineUsed) || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-300">Credit on line:</span>
                              <span className="font-semibold text-amber-200">
                                ₹{(Number(selectedPlayer.creditLineOnLine) || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-300">Credit remaining:</span>
                              <span className="font-semibold text-emerald-300">
                                ₹{(Number(selectedPlayer.creditLineRemaining) || 0).toLocaleString()}
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">3. Final Balance / Buy-Out Amount</label>
                      <input
                        type="number"
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        placeholder="Enter final balance amount..."
                        value={buyOutAmount}
                        onChange={(e) => setBuyOutAmount(e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Defaults to session chip total. Adjust if staff and player agree on a different settlement.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">4. Reason (Optional)</label>
                      <textarea
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        rows="3"
                        placeholder="Enter reason for buy-out (optional)..."
                        value={buyOutReason}
                        onChange={(e) => setBuyOutReason(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleApproveBuyOut}
                      disabled={
                        !selectedPlayer ||
                        String(buyOutAmount ?? '').trim() === '' ||
                        isNaN(parseFloat(String(buyOutAmount).trim())) ||
                        parseFloat(String(buyOutAmount).trim()) < 0
                      }
                      className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-3 rounded-lg font-semibold transition-colors"
                    >
                      Approve Buy-Out
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white/10 p-5 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Buy-Out Information</h3>
              <div className="space-y-4">
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-2">How Buy-Out Works:</h4>
                  <ul className="text-sm text-gray-300 space-y-2 list-disc list-inside">
                    <li>Select a player who is currently seated at a rummy table</li>
                    <li>Their table chip balance will be moved to their available balance</li>
                    <li>Player will be removed from the table (seat becomes available)</li>
                    <li>Player can then convert chips to real money at the cashier counter</li>
                  </ul>
                </div>

                <div className="p-4 bg-yellow-500/20 rounded-lg border border-yellow-400/30">
                  <h4 className="text-yellow-300 font-semibold mb-2">⚠️ Important Notes:</h4>
                  <ul className="text-sm text-yellow-200 space-y-1 list-disc list-inside">
                    <li>Buy-out approval is permanent and cannot be undone</li>
                    <li>Outstanding credit on the line is settled the same way as on poker tables</li>
                    <li>Hologram and player app refresh when transactions complete</li>
                  </ul>
                </div>

                <div className="p-4 bg-green-500/20 rounded-lg border border-green-400/30">
                  <div className="text-sm text-green-300">
                    <div className="font-semibold mb-1">Currently Seated Players (rummy):</div>
                    <div className="text-green-200">{seatedCountAllRummy} player(s) at tables with seats</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "player-requests" && (
          <TableBuyOutManagement clubId={selectedClubId} gameType="rummy" />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Rummy History View Component (History Tab)
// ============================================================================
function RummyHistoryView({ selectedClubId, tables }) {
  const [historyFilters, setHistoryFilters] = useState({
    type: 'all',
    search: '',
    tableNumber: 'all',
    page: 1,
    perPage: 10,
  });

  const rummyTableNumbers = (tables || []).filter(t => t.tableType === 'RUMMY').map(t => t.tableNumber);

  const { data: allTransactions = [], isLoading: historyLoading } = useQuery({
    queryKey: ['rummy-transactions', selectedClubId, rummyTableNumbers?.length],
    queryFn: async () => {
      if (!selectedClubId) return [];
      try {
        const response = await superAdminAPI.getTransactions(selectedClubId, {});
        let transactions = Array.isArray(response) ? response : (response?.data || response?.transactions || []);
        return transactions.filter(t => {
          const gt = t.gameType || t.game_type;
          if (gt === 'rummy') return true;
          if (gt) return false;
          // Backward compatibility: no game_type set - include if notes mention a rummy table number
          const tableNumMatch = t.notes?.match(/Table\s*(\d+)/i) || t.description?.match(/Table\s*(\d+)/i);
          const n = tableNumMatch ? parseInt(tableNumMatch[1], 10) : null;
          return n != null && rummyTableNumbers.includes(n);
        });
      } catch (error) {
        console.error('[RUMMY HISTORY] Error fetching transactions:', error);
        return [];
      }
    },
    enabled: !!selectedClubId,
  });

  const filteredHistory = allTransactions.filter(t => {
    const isBuyIn = t.type === 'Buy In';
    const isBuyOut = (t.type === 'Deposit' || t.type === 'Cashout') && 
                     (t.notes?.toLowerCase().includes('buy-out') || 
                      t.notes?.toLowerCase().includes('buyout') ||
                      t.description?.toLowerCase().includes('buy-out') ||
                      t.description?.toLowerCase().includes('buyout'));
    
    if (historyFilters.type === 'buy-in' && !isBuyIn) return false;
    if (historyFilters.type === 'buy-out' && !isBuyOut) return false;
    if (historyFilters.type === 'all' && !(isBuyIn || isBuyOut)) return false;
    
    if (historyFilters.tableNumber !== 'all') {
      const tableMatch = t.notes?.includes(`Table ${historyFilters.tableNumber}`) || 
                        t.description?.includes(`Table ${historyFilters.tableNumber}`) ||
                        t.notes?.includes(`table ${historyFilters.tableNumber}`) ||
                        t.description?.includes(`table ${historyFilters.tableNumber}`);
      if (!tableMatch) return false;
    }
    
    if (historyFilters.search) {
      const search = historyFilters.search.toLowerCase();
      const playerName = t.playerName || t.player_name || '';
      const email = t.email || '';
      return (
        playerName.toLowerCase().includes(search) ||
        email.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  const paginatedHistory = filteredHistory.slice(
    (historyFilters.page - 1) * historyFilters.perPage,
    historyFilters.page * historyFilters.perPage
  );
  
  const totalPages = Math.ceil(filteredHistory.length / historyFilters.perPage);
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-600/30 via-teal-500/20 to-cyan-700/30 rounded-xl p-6 border border-emerald-800/40">
        <h2 className="text-2xl font-bold mb-4">Rummy Buy-In & Buy-Out History</h2>
        <p className="text-gray-300 text-sm mb-6">
          View all rummy buy-in and buy-out transactions with advanced filters.
        </p>
        <div className="bg-white/10 p-5 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Transaction Type</label>
              <select 
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                value={historyFilters.type}
                onChange={(e) => setHistoryFilters({...historyFilters, type: e.target.value, page: 1})}
              >
                <option value="all">All Transactions</option>
                <option value="buy-in">Buy-In Only</option>
                <option value="buy-out">Buy-Out Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Table</label>
              <select 
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                value={historyFilters.tableNumber}
                onChange={(e) => setHistoryFilters({...historyFilters, tableNumber: e.target.value, page: 1})}
              >
                <option value="all">All Tables</option>
                {tables.map((table) => (
                  <option key={table.id} value={table.tableNumber}>
                    Table {table.tableNumber}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Search Player</label>
              <input 
                type="text"
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                placeholder="Player name or email..."
                value={historyFilters.search}
                onChange={(e) => setHistoryFilters({...historyFilters, search: e.target.value, page: 1})}
              />
            </div>
          </div>
        </div>
        
        <div className="bg-white/10 p-5 rounded-lg mt-4">
          <h3 className="text-lg font-semibold mb-4">
            Transaction History ({filteredHistory.length} records)
          </h3>
          
          {historyLoading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : paginatedHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No rummy transactions found</div>
          ) : (
            <>
              <div className="space-y-3 mb-6">
                {paginatedHistory.map((transaction, idx) => {
                  const isBuyIn = transaction.type === 'Buy In';
                  return (
                    <div 
                      key={transaction.id || idx} 
                      className={`p-4 rounded-lg border-2 ${
                        isBuyIn 
                          ? 'bg-green-900/20 border-green-600/50' 
                          : 'bg-orange-900/20 border-orange-600/50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">
                              {isBuyIn ? '💰' : '💵'}
                            </span>
                            <div>
                              <div className="font-bold text-white">
                                {transaction.playerName || transaction.player_name || 'Unknown Player'}
                              </div>
                              <div className="text-sm text-gray-400">
                                {isBuyIn ? 'Buy-In' : 'Buy-Out'}
                                {transaction.notes &&
                                  ` • ${sanitizeTransactionNotesForDisplay(transaction.notes)}`}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(transaction.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={`text-right ${isBuyIn ? 'text-green-400' : 'text-orange-400'}`}>
                          <div className="font-bold text-2xl">
                            {isBuyIn ? '+' : ''}₹{Number(transaction.amount).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-400">
                            {transaction.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <button
                    onClick={() => setHistoryFilters({...historyFilters, page: Math.max(1, historyFilters.page - 1)})}
                    disabled={historyFilters.page === 1}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg text-white"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 bg-slate-700 rounded-lg text-white">
                    Page {historyFilters.page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setHistoryFilters({...historyFilters, page: Math.min(totalPages, historyFilters.page + 1)})}
                    disabled={historyFilters.page >= totalPages}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg text-white"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
